import React, { useId, useRef } from 'react';
import styled from '@emotion/styled';
import { SignupFlow } from 'corgi-types';
import type { SpaceProps, WidthProps } from 'styled-system';
import { borderColor, color, height, space, width } from 'styled-system';

import { ButtonLink } from 'common/components/Button';
import ClientOnly from 'common/components/ClientOnly';
import { Flex } from 'common/components/Flex';
import { Grid, GridItem } from 'common/components/Grid';
import Input from 'common/components/Input/StatefulInput';
import { BodyBoldBB1 } from 'common/components/Typography';
import { useLoginUrl } from 'common/hooks/form/login';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import FixedBottomContainer from '../../home-club-section/components/HomeClubContent/FixedBottomContainer';
import CopyButton from './CopyButton';
import ReferralNotAllowedMessage from './ReferralNotAllowedMessage';
import ShareButton from './ShareButton';
import { TrackingInteraction, trackReferAFriendClick } from './tracking';

export interface ReferAFriendSectionProps {
  isLoggedIn: boolean;
  referralLink: string;
  referralAllowed: boolean;
}

const INPUT_HEIGHT = 48;

const InputContainer = styled.div<SpaceProps & WidthProps>`
  ${space};
  ${width};
`;

const StyledInput = styled(Input)`
  ${color};
  ${width};
  ${height};
  ${borderColor};
`;

const ReferAFriendSection = ({
  isLoggedIn,
  referralLink,
  referralAllowed,
}: ReferAFriendSectionProps) => {
  const { t } = useLocalization();
  const disabledMessageId = useId();
  const {
    matches: { matchesDesktop, matchesLargeDesktop },
  } = useMatchesViewport();
  const inputRef = useRef<HTMLInputElement>(null);

  const isDisabled = !referralAllowed;
  const loginUrl = useLoginUrl(SignupFlow.ReferralLanding);

  const copyButton = (
    <CopyButton
      referralLink={referralLink}
      isDisabled={isDisabled}
      onCopyEnd={() => inputRef.current?.blur()}
      onCopyStart={() => inputRef.current?.select()}
    />
  );

  const loginButton = (
    <ClientOnly>
      <ButtonLink
        themeWidth="100%"
        href={loginUrl}
        onClick={() =>
          trackReferAFriendClick(t('h24_login_form_submit_btn'), TrackingInteraction.Login)
        }
      >
        {t('h24_login_form_submit_btn')}
      </ButtonLink>
    </ClientOnly>
  );

  if (matchesDesktop || matchesLargeDesktop) {
    return (
      <Grid data-corgi-id="refer-a-friend-section" py="sp_16">
        <GridItem width={[null, null, 3 / 4, 1 / 2]} mx="auto">
          <BodyBoldBB1 textAlign="center" color="sys.neutral.text.strong" mb="sp_16">
            {t('h24_refer_copy_message')}
          </BodyBoldBB1>
          <Flex justifyContent="center">
            <InputContainer width="100%" mr="sp_16">
              <StyledInput
                readOnly
                py="sp_10"
                type="text"
                inputRef={inputRef}
                height={INPUT_HEIGHT}
                disabled={isDisabled}
                color="sys.neutral.text.default"
                onFocus={e => e.target.select()}
                onCopy={() =>
                  trackReferAFriendClick(
                    t('h24_refer_copy_btn_label'),
                    TrackingInteraction.CopyReferralLink
                  )
                }
                borderColor="sys.neutral.border.default"
                value={referralAllowed ? referralLink : ''}
                {...(isDisabled
                  ? { 'aria-labelledby': disabledMessageId }
                  : { label: t('h24_refer_copy_input_label') })}
              />
            </InputContainer>

            {isLoggedIn ? copyButton : loginButton}
          </Flex>

          {isDisabled && <ReferralNotAllowedMessage id={disabledMessageId} />}
        </GridItem>
      </Grid>
    );
  }

  return (
    <FixedBottomContainer
      data-corgi-id="refer-a-friend-section"
      display="flex"
      flexDirection="column"
    >
      {!isLoggedIn ? (
        loginButton
      ) : window?.navigator?.canShare ? (
        <ShareButton referralLink={referralLink} isDisabled={isDisabled} />
      ) : (
        copyButton
      )}
      {!referralAllowed && <ReferralNotAllowedMessage />}
    </FixedBottomContainer>
  );
};

export default ReferAFriendSection;
